/**
 * Client-side consignment completion rates and daily trend series from
 * orders_daily rows.
 *
 * Completion % and consignment counts use distinct tracking_id grain (never
 * lifts). The pallets chart metric deliberately sums `lifts` on completed
 * consignments only. Prefer `summary.*.completion_pct` when the server already
 * summarised the same set; use these helpers to slice by day / direction / metric.
 */

import { addUtcDays, ratioPercent } from '../lib/calc';
import type {
  OrdersDailyRow,
  OrdersDailySeriesDay,
} from '../types/ordersDailyTruth';
import { londonDateString } from './ordersDailyPresentation';

export type OrdersRateMode = 'delivery' | 'collection';

function isCompleteForMode(row: OrdersDailyRow, mode: OrdersRateMode): boolean {
  return mode === 'delivery' ? row.delivered : row.collected;
}

/** Distinct-consignment completion % for a row set (null when empty). */
export function consignmentCompletionPct(
  rows: readonly OrdersDailyRow[],
  isComplete: (row: OrdersDailyRow) => boolean,
): number | null {
  const total = new Set<string>();
  const completed = new Set<string>();
  for (const row of rows) {
    total.add(row.tracking_id);
    if (isComplete(row)) completed.add(row.tracking_id);
  }
  return ratioPercent(completed.size, total.size);
}

/** Distinct completed / total consignment counts for one direction. */
export function consignmentCountsForDirection(
  rows: readonly OrdersDailyRow[],
  mode: OrdersRateMode,
): { completed: number; total: number } {
  const subset = rows.filter((r) => r.direction === mode);
  const total = new Set<string>();
  const completed = new Set<string>();
  for (const row of subset) {
    total.add(row.tracking_id);
    if (isCompleteForMode(row, mode)) completed.add(row.tracking_id);
  }
  return { completed: completed.size, total: total.size };
}

export function completionPctForDirection(
  rows: readonly OrdersDailyRow[],
  mode: OrdersRateMode,
): number | null {
  const { completed, total } = consignmentCountsForDirection(rows, mode);
  return ratioPercent(completed, total);
}

export interface DailyRatePoint {
  date: string;
  label: string;
  /**
   * Plot value: completion rate as 0–100, or completed consignment / pallet
   * counts (absolute), depending on {@link ChartMetricKind}.
   */
  value: number;
  consignments: number;
  completed: number;
}

function formatDayLabel(
  dateKey: string,
  opts?: { todayDate?: string; weekdayOnlyToday?: boolean },
): string {
  const d = new Date(`${dateKey}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return dateKey;
  const weekday = d.toLocaleDateString('en-GB', { weekday: 'short' });
  // 30d only: today as weekday alone — date paren collides with neighbouring Mondays.
  if (opts?.weekdayOnlyToday && opts.todayDate && dateKey === opts.todayDate) {
    return weekday;
  }
  const ddmm = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
  // Weekday leads (ops language); calendar date secondary on its own line.
  // BarChart splits on `\n` into two stacked x-axis lines.
  return `${weekday}\n(${ddmm})`;
}

/**
 * Last N London calendar days of completion rate for one direction.
 * Days with no consignments are omitted (avoid plotting a false 0%).
 */
export function buildDailyCompletionRateSeries(
  rows: readonly OrdersDailyRow[],
  mode: OrdersRateMode,
  opts?: { days?: number; asOf?: Date; todayDate?: string },
): DailyRatePoint[] {
  return buildDailyMetricSeries(rows, mode, 'completion_rate', opts);
}

/** Wallboard chart y-axis metric. */
export type ChartMetricKind = 'completion_rate' | 'consignments' | 'pallets';

export const CHART_METRIC_OPTIONS: readonly { id: ChartMetricKind; label: string }[] = [
  { id: 'completion_rate', label: 'Completion rates' },
  { id: 'consignments', label: 'Completed consignment counts' },
  { id: 'pallets', label: 'Completed pallet counts' },
];

function dayWindow(
  opts?: { days?: number; asOf?: Date },
): { days: number; start: string; end: string } {
  const days = opts?.days ?? 7;
  const asOf = opts?.asOf ?? new Date();
  const end = londonDateString(asOf);
  const start = addUtcDays(end, -(days - 1));
  return { days, start, end };
}

function rowsByDateForMode(
  rows: readonly OrdersDailyRow[],
  mode: OrdersRateMode,
  start: string,
  end: string,
): Map<string, OrdersDailyRow[]> {
  const byDate = new Map<string, OrdersDailyRow[]>();
  for (const row of rows) {
    if (row.direction !== mode) continue;
    if (row.grain_date < start || row.grain_date > end) continue;
    const list = byDate.get(row.grain_date);
    if (list) list.push(row);
    else byDate.set(row.grain_date, [row]);
  }
  return byDate;
}

/**
 * Last N London calendar days of one metric for one direction.
 * Days with no consignments are omitted (avoid plotting a false 0).
 */
export function buildDailyMetricSeries(
  rows: readonly OrdersDailyRow[],
  mode: OrdersRateMode,
  metric: ChartMetricKind,
  opts?: { days?: number; asOf?: Date; todayDate?: string },
): DailyRatePoint[] {
  const { days, start, end } = dayWindow(opts);
  const byDate = rowsByDateForMode(rows, mode, start, end);
  const todayDate = opts?.todayDate ?? londonDateString(opts?.asOf);
  const weekdayOnlyToday = days === 30;

  const out: DailyRatePoint[] = [];
  for (let i = 0; i < days; i++) {
    const date = addUtcDays(start, i);
    const dayRows = byDate.get(date) ?? [];
    if (dayRows.length === 0) continue;

    const trackingIds = new Set(dayRows.map((r) => r.tracking_id));
    const consignments = trackingIds.size;
    const completedRows = dayRows.filter((r) => isCompleteForMode(r, mode));
    const completed = new Set(completedRows.map((r) => r.tracking_id)).size;
    const liftsCompleted = completedRows.reduce((sum, r) => sum + (r.lifts ?? 0), 0);

    let value: number | null;
    if (metric === 'completion_rate') {
      value = ratioPercent(completed, consignments);
    } else if (metric === 'consignments') {
      // Numerator: distinct completed consignments (same grain as the rate %).
      value = completed;
    } else {
      // Numerator: pallet spaces on completed consignments (not the day's booked total).
      value = liftsCompleted;
    }
    if (value == null) continue;

    out.push({
      date,
      label: formatDayLabel(date, { todayDate, weekdayOnlyToday }),
      value,
      consignments,
      completed,
    });
  }
  return out;
}

/**
 * Daily ETA% (on-time vs PDT) among delivered deliveries with a parseable estimate.
 * Same denominator as `summary.eta` — days with no eligible consignments are omitted
 * (do not invent 0% when PDT is missing).
 *
 * On each point, `completed` is the on-time consignment count (not “delivery
 * completed”); `consignments` is the ETA-eligible denominator.
 */
export function buildDailyEtaRateSeries(
  rows: readonly OrdersDailyRow[],
  opts?: { days?: number; asOf?: Date; todayDate?: string },
): DailyRatePoint[] {
  const { days, start, end } = dayWindow(opts);
  const byDate = rowsByDateForMode(rows, 'delivery', start, end);
  const todayDate = opts?.todayDate ?? londonDateString(opts?.asOf);
  const weekdayOnlyToday = days === 30;

  const out: DailyRatePoint[] = [];
  for (let i = 0; i < days; i++) {
    const date = addUtcDays(start, i);
    const dayRows = byDate.get(date) ?? [];
    const eligible = dayRows.filter((r) => r.eta_on_time !== null && r.eta_on_time !== undefined);
    if (eligible.length === 0) continue;

    const totalIds = new Set(eligible.map((r) => r.tracking_id));
    const onTimeIds = new Set(
      eligible.filter((r) => r.eta_on_time === true).map((r) => r.tracking_id),
    );
    const consignments = totalIds.size;
    const completed = onTimeIds.size;
    const value = ratioPercent(completed, consignments);
    if (value == null) continue;

    out.push({
      date,
      label: formatDayLabel(date, { todayDate, weekdayOnlyToday }),
      value,
      consignments,
      completed,
    });
  }
  return out;
}

export function londonLastNDaysRange(
  days: number,
  asOf: Date = new Date(),
): { startDate: string; endDate: string } {
  const endDate = londonDateString(asOf);
  return { startDate: addUtcDays(endDate, -(days - 1)), endDate };
}

/** Lookback chips on the Orders wallboard completion-rate chart. */
export type ChartLookbackDays = 7 | 14 | 30;

export const CHART_LOOKBACK_OPTIONS: readonly ChartLookbackDays[] = [7, 14, 30];

/** Delivery/collection/ETA bar tones (emerald / amber / sky). */
export const DELIVERY_RATE_BAR_COLOR = '#34d399';
export const COLLECTION_RATE_BAR_COLOR = '#fbbf24';
export const ETA_RATE_BAR_COLOR = '#38bdf8';
/** Slightly muted variants for London “today” (still provisional). */
export const DELIVERY_RATE_BAR_COLOR_TODAY = '#059669';
export const COLLECTION_RATE_BAR_COLOR_TODAY = '#d97706';
export const ETA_RATE_BAR_COLOR_TODAY = '#0284c7';

/** Stable legend order / colours for the Orders trend chart (non-today tones). */
export const TREND_SERIES_LEGEND: readonly { name: string; color: string }[] = [
  { name: 'Delivery', color: DELIVERY_RATE_BAR_COLOR },
  { name: 'Collection', color: COLLECTION_RATE_BAR_COLOR },
  { name: 'ETA%', color: ETA_RATE_BAR_COLOR },
];

/**
 * Round rate % for chart bars without collapsing a positive sub-0.5% to 0
 * (which looked like “no on-time” / invented zero).
 */
export function chartRateBarValue(percent: number): number {
  if (percent > 0 && percent < 0.5) return 0.5;
  return Math.round(percent * 10) / 10;
}

/**
 * Series present in any day group, in {@link TREND_SERIES_LEGEND} order.
 * Uses canonical colours so “today” mute tones do not tint the legend.
 */
export function trendSeriesLegendItems(
  groups: readonly CompletionRateBarGroup[],
): { name: string; color: string }[] {
  const present = new Set<string>();
  for (const g of groups) {
    for (const v of g.values) present.add(v.vehicle);
  }
  return TREND_SERIES_LEGEND.filter((s) => present.has(s.name));
}

/** One x-axis day for {@link BarChart}: series name lives in `vehicle`. */
export interface CompletionRateBarGroup {
  label: string;
  date: string;
  values: { vehicle: string; value: number; color: string }[];
  /**
   * When true (30d thinning), keep `label` for hover but omit it from the
   * always-visible x-axis until this day is hovered.
   */
  hideAxisLabelUntilHover?: boolean;
}

/**
 * Merge delivery + collection (+ optional ETA%) daily series into grouped bar rows.
 * A day appears if any series has data; missing series are omitted (not plotted as 0%).
 */
export function mergeDailyRateSeriesForBarChart(
  delivery: readonly DailyRatePoint[],
  collection: readonly DailyRatePoint[],
  opts?: { todayDate?: string; eta?: readonly DailyRatePoint[] },
): CompletionRateBarGroup[] {
  const byDate = new Map<string, {
    label: string;
    delivery?: DailyRatePoint;
    collection?: DailyRatePoint;
    eta?: DailyRatePoint;
  }>();
  for (const p of delivery) {
    const existing = byDate.get(p.date);
    if (existing) existing.delivery = p;
    else byDate.set(p.date, { label: p.label, delivery: p });
  }
  for (const p of collection) {
    const existing = byDate.get(p.date);
    if (existing) {
      existing.collection = p;
      if (!existing.label) existing.label = p.label;
    } else {
      byDate.set(p.date, { label: p.label, collection: p });
    }
  }
  for (const p of opts?.eta ?? []) {
    const existing = byDate.get(p.date);
    if (existing) {
      existing.eta = p;
      if (!existing.label) existing.label = p.label;
    } else {
      byDate.set(p.date, { label: p.label, eta: p });
    }
  }

  const today = opts?.todayDate;
  return [...byDate.keys()]
    .sort()
    .map((date) => {
      const g = byDate.get(date)!;
      const isToday = today != null && date === today;
      const values: CompletionRateBarGroup['values'] = [];
      if (g.delivery) {
        values.push({
          vehicle: 'Delivery',
          value: chartRateBarValue(g.delivery.value),
          color: isToday ? DELIVERY_RATE_BAR_COLOR_TODAY : DELIVERY_RATE_BAR_COLOR,
        });
      }
      if (g.collection) {
        values.push({
          vehicle: 'Collection',
          value: chartRateBarValue(g.collection.value),
          color: isToday ? COLLECTION_RATE_BAR_COLOR_TODAY : COLLECTION_RATE_BAR_COLOR,
        });
      }
      if (g.eta) {
        values.push({
          vehicle: 'ETA%',
          value: chartRateBarValue(g.eta.value),
          color: isToday ? ETA_RATE_BAR_COLOR_TODAY : ETA_RATE_BAR_COLOR,
        });
      }
      return { label: g.label, date, values };
    });
}

function calendarDaysBetween(a: string, b: string): number {
  const ms = Date.parse(`${b}T12:00:00.000Z`) - Date.parse(`${a}T12:00:00.000Z`);
  return Math.round(ms / 86_400_000);
}

/**
 * For dense lookbacks (30d), keep weekday+date labels only on Mondays plus the
 * first day — week boundaries are how operators scan a month. The last day is
 * labelled only when it is not adjacent (≤1 day) to another kept label, so
 * “Mon” + “Tue” (today) do not stack.
 *
 * Mid-week labels stay on the group (for hover) but are flagged so the chart
 * omits them from the x-axis until that day is hovered.
 */
export function thinDailyBarChartLabels(
  groups: readonly CompletionRateBarGroup[],
  lookbackDays: ChartLookbackDays,
): CompletionRateBarGroup[] {
  if (lookbackDays !== 30) return [...groups];
  const out = groups.map((g, i) => {
    const isFirst = i === 0;
    const isLast = i === groups.length - 1;
    const weekday = new Date(`${g.date}T12:00:00.000Z`).getUTCDay(); // 1 = Mon
    if (isFirst || isLast || weekday === 1) return g;
    return { ...g, hideAxisLabelUntilHover: true };
  });

  if (out.length < 2 || out[out.length - 1].hideAxisLabelUntilHover) return out;
  let prevKept = -1;
  for (let i = out.length - 2; i >= 0; i--) {
    if (!out[i].hideAxisLabelUntilHover) {
      prevKept = i;
      break;
    }
  }
  if (prevKept >= 0 && calendarDaysBetween(out[prevKept].date, out[out.length - 1].date) <= 1) {
    out[out.length - 1] = { ...out[out.length - 1], hideAxisLabelUntilHover: true };
  }
  return out;
}

export function rateInsightTone(
  pct: number | null,
  hasDenominator: boolean,
): 'default' | 'success' | 'warning' | 'danger' {
  if (!hasDenominator || pct == null) return 'default';
  if (pct >= 80) return 'success';
  if (pct >= 60) return 'warning';
  return 'danger';
}

/** CSV export window — independent of the on-screen lookback chip. */
export const TREND_CSV_LOOKBACK_DAYS = 30 as const;

const TREND_CSV_SERIES = ['Delivery', 'Collection', 'ETA%'] as const;

/**
 * Build trend bar groups for chart or CSV from the same pipeline.
 * Pass `thinLabels: true` for the on-screen 30d chart only.
 */
export function buildTrendBarGroups(
  rows: readonly OrdersDailyRow[],
  metric: ChartMetricKind,
  days: number,
  opts: { asOf: Date; todayDate?: string; thinLabels?: boolean },
): CompletionRateBarGroup[] {
  const todayDate = opts.todayDate ?? londonDateString(opts.asOf);
  const seriesOpts = { days, asOf: opts.asOf, todayDate };
  const delivery = buildDailyMetricSeries(rows, 'delivery', metric, seriesOpts);
  const collection = buildDailyMetricSeries(rows, 'collection', metric, seriesOpts);
  const eta = metric === 'completion_rate'
    ? buildDailyEtaRateSeries(rows, seriesOpts)
    : undefined;
  const merged = mergeDailyRateSeriesForBarChart(delivery, collection, { todayDate, eta });
  if (opts.thinLabels && (days === 7 || days === 14 || days === 30)) {
    return thinDailyBarChartLabels(merged, days);
  }
  return merged;
}

/**
 * {@link buildDailyCompletionRateSeries} from the stored `/daily/series` days
 * instead of consignment rows — the Control Centre overview trend, so first
 * load never asks for a live multi-day compute.
 */
export function buildDailyCompletionRateSeriesFromSeries(
  days: readonly OrdersDailySeriesDay[],
  mode: OrdersRateMode,
  opts?: { days?: number; asOf?: Date; todayDate?: string },
): DailyRatePoint[] {
  const { days: n, start, end } = dayWindow(opts);
  const todayDate = opts?.todayDate ?? londonDateString(opts?.asOf);
  return seriesDaysToMetricPoints(days, mode, 'completion_rate', {
    start,
    end,
    todayDate,
    weekdayOnlyToday: n === 30,
  });
}

function seriesDaysToMetricPoints(
  days: readonly OrdersDailySeriesDay[],
  mode: OrdersRateMode,
  metric: ChartMetricKind,
  opts: { start: string; end: string; todayDate: string; weekdayOnlyToday: boolean },
): DailyRatePoint[] {
  const out: DailyRatePoint[] = [];
  for (const day of days) {
    if (day.date < opts.start || day.date > opts.end) continue;
    const progress = mode === 'delivery' ? day.summary.deliveries : day.summary.collections;
    if (progress.total.consignments === 0) continue;
    const consignments = progress.total.consignments;
    const completed = progress.completed.consignments;
    let value: number | null;
    if (metric === 'completion_rate') {
      value = progress.completion_pct;
    } else if (metric === 'consignments') {
      value = completed;
    } else {
      value = progress.completed.lifts;
    }
    if (value == null) continue;
    out.push({
      date: day.date,
      label: formatDayLabel(day.date, {
        todayDate: opts.todayDate,
        weekdayOnlyToday: opts.weekdayOnlyToday,
      }),
      value,
      consignments,
      completed,
    });
  }
  return out;
}

function seriesDaysToEtaPoints(
  days: readonly OrdersDailySeriesDay[],
  opts: { start: string; end: string; todayDate: string; weekdayOnlyToday: boolean },
): DailyRatePoint[] {
  const out: DailyRatePoint[] = [];
  for (const day of days) {
    if (day.date < opts.start || day.date > opts.end) continue;
    const eta = day.summary.eta;
    if (!eta || eta.total.consignments === 0 || eta.completion_pct == null) continue;
    out.push({
      date: day.date,
      label: formatDayLabel(day.date, {
        todayDate: opts.todayDate,
        weekdayOnlyToday: opts.weekdayOnlyToday,
      }),
      value: eta.completion_pct,
      consignments: eta.total.consignments,
      completed: eta.on_time.consignments,
    });
  }
  return out;
}

/**
 * Same chart/CSV bars as {@link buildTrendBarGroups}, from `/daily/series` days
 * (no consignment rows). Must stay lockstep — see orders_daily_series.md.
 */
export function buildTrendBarGroupsFromSeries(
  days: readonly OrdersDailySeriesDay[],
  metric: ChartMetricKind,
  lookbackDays: number,
  opts: { asOf: Date; todayDate?: string; thinLabels?: boolean; endDate?: string },
): CompletionRateBarGroup[] {
  const todayDate = opts.todayDate ?? londonDateString(opts.asOf);
  const end = opts.endDate ?? todayDate;
  const start = addUtcDays(end, -(lookbackDays - 1));
  const weekdayOnlyToday = lookbackDays === 30;
  const windowOpts = { start, end, todayDate, weekdayOnlyToday };
  const delivery = seriesDaysToMetricPoints(days, 'delivery', metric, windowOpts);
  const collection = seriesDaysToMetricPoints(days, 'collection', metric, windowOpts);
  const eta = metric === 'completion_rate'
    ? seriesDaysToEtaPoints(days, windowOpts)
    : undefined;
  const merged = mergeDailyRateSeriesForBarChart(delivery, collection, { todayDate, eta });
  if (opts.thinLabels && (lookbackDays === 7 || lookbackDays === 14 || lookbackDays === 30)) {
    return thinDailyBarChartLabels(merged, lookbackDays);
  }
  return merged;
}

/** Pure CSV body for the trend export (testable without DOM). */
export function buildTrendChartCsvContent(
  groups: readonly CompletionRateBarGroup[],
  metric: ChartMetricKind,
): string {
  const present = new Set<string>();
  for (const g of groups) {
    for (const v of g.values) present.add(v.vehicle);
  }
  const series = TREND_CSV_SERIES.filter((name) => present.has(name));
  const valueSuffix =
    metric === 'completion_rate'
      ? ' %'
      : metric === 'consignments'
        ? ' consignments'
        : ' pallets';
  const headers = [
    'Date',
    ...series.map((name) => (name === 'ETA%' ? 'ETA %' : `${name}${valueSuffix}`)),
  ];
  const lines = [
    headers.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(','),
    ...groups.map((g) => {
      const byName = new Map(g.values.map((v) => [v.vehicle, v.value]));
      const cells = [
        g.date,
        ...series.map((name) => {
          const v = byName.get(name);
          return v == null ? '' : String(v);
        }),
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
    }),
  ];
  return lines.join('\n');
}

export function trendChartCsvFilename(
  metric: ChartMetricKind,
  lookbackDays: number,
  endDate: string,
): string {
  return `orders-trend-${metric}-${lookbackDays}d-${endDate}.csv`;
}

/** Trigger a browser download of the trend CSV. */
export function downloadTrendChartCsv(
  groups: readonly CompletionRateBarGroup[],
  opts: { metric: ChartMetricKind; lookbackDays: number; endDate: string },
): void {
  const content = buildTrendChartCsvContent(groups, opts.metric);
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = trendChartCsvFilename(opts.metric, opts.lookbackDays, opts.endDate);
  a.click();
  URL.revokeObjectURL(url);
}
